import { updateLoadingState } from 'store/app.slice';
import { BaseHandler } from './base.handler';
import { formatDateShortener } from 'utils/helpers/dateTime';
import FuelAnalyticsAPI from 'api/fuelAnalytics.api';
import { type Asset } from 'types/asset.types';
import TireProfileAPI from 'api/tire-pressure';
import GeoAPI from 'api/geo.api';
import { setFleetData } from 'store/tireProfile.slice';
import DriverBehaviourAPI from 'api/driver.behaviour.api';
import DtcAPI from 'api/dtc.api';
import { getAll } from 'store/dtc.slice';

export default class VehicleStatusHandler extends BaseHandler {
  private readonly api: FuelAnalyticsAPI;
  private readonly tireApi: TireProfileAPI;
  private readonly geoApi: GeoAPI;
  private readonly driverBehaviourApi: DriverBehaviourAPI;
  private readonly fuelAnalyticsApi: FuelAnalyticsAPI;
  private readonly dtcApi: DtcAPI;
  constructor() {
    super();
    this.api = new FuelAnalyticsAPI();
    this.tireApi = new TireProfileAPI();
    this.geoApi = new GeoAPI();
    this.driverBehaviourApi = new DriverBehaviourAPI();
    this.fuelAnalyticsApi = new FuelAnalyticsAPI();
    this.dtcApi = new DtcAPI();
  }

  async getHealthData(
    selectedAsset: Asset,
    startDate: string,
    endDate: string
  ): Promise<
    | {
        tireData: any;
        // fuelEfficiency: any;
        braking: any;
        overspeed: any;
        acceleration: any;
        gearSkip: any;
        idleFuel: any;
        underspeed: any;
        incorrectGearToSpeed: any;
        airFilters: any;
        fuelData: any;
        dtc: any;
      }
    | undefined
  > {
    if (!selectedAsset) {
      return undefined;
    }
    this.dispatch(updateLoadingState(true));
    try {
      const daysAgo = (days: number) => {
        return formatDateShortener(
          new Date(new Date().getTime() - days * 24 * 60 * 60 * 1000)
        );
      };

      const shouldSendDate = false;
      const [
        tireData,
        idleFuel,
        braking,
        overspeed,
        acceleration,
        gearSkip,
        underspeed,
        incorrectGearToSpeed,
        airFilters,
        fuelData,
        dtc,
      ] = await Promise.allSettled([
        this.tireApi.getFleetData(),
        this.api.getDeviceFuelIdling(
          selectedAsset.device.deviceId,
          daysAgo(0),
          daysAgo(0)
        ),

        this.driverBehaviourApi.getAllWithPastComparision({
          event: 'braking',
          startDate,
          endDate,
        }),
        this.driverBehaviourApi.getAllWithPastComparision({
          event: 'overspeed',
          startDate,
          endDate,
        }),
        this.driverBehaviourApi.getAllWithPastComparision({
          event: 'acceleration',
          startDate,
          endDate,
        }),
        this.driverBehaviourApi.getAllWithPastComparision({
          event: 'gear_skip',
          startDate,
          endDate,
        }),
        this.driverBehaviourApi.getAllWithPastComparision({
          event: 'underspeed',
          startDate,
          endDate,
        }),
        this.driverBehaviourApi.getAllWithPastComparision({
          event: 'incorrect_gear',
          startDate,
          endDate,
        }),
        this.fuelAnalyticsApi.getAirFilters([selectedAsset.device.deviceId]),

        this.fuelAnalyticsApi.getFleetFuelConsumptionPerDevicePerDay(
          daysAgo(0),
          daysAgo(0),
          'Today',
          'daily',
          true,
          false,
          1
        ),

        this.dtcApi.getAllGroupedData({
          startDate: daysAgo(0),
          endDate: daysAgo(0),
        }),
      ]);

      this.dispatch(setFleetData((tireData as any).value));

      this.dispatch(getAll(dtc as any));

      this.handleSuccess();
      this.dispatch(updateLoadingState(false));
      return {
        tireData: (tireData as any).value,
        braking: (braking as any).value,
        overspeed: (overspeed as any).value,
        acceleration: (acceleration as any).value,
        gearSkip: (gearSkip as any).value,
        idleFuel: (idleFuel as any).value,
        underspeed: (underspeed as any).value,
        incorrectGearToSpeed: (incorrectGearToSpeed as any).value,
        airFilters: (airFilters as any).value,
        fuelData: (fuelData as any).value,
        dtc: (dtc as any).value,
      };
    } catch (error: any) {
      let msg: string = '';
      if (error.isAxiosError) {
        msg = error.response.data.message as string;
      }

      this.handleError(
        'An error occured while getting the fuel analytics data: ' + msg
      );
      this.dispatch(updateLoadingState(false));
      return undefined;
    }
  }
}
